import React, { Component } from 'react'
import { connect } from 'react-redux'
import { fetchGigTracking } from 'actions/track'
import styled, { css, withTheme } from 'styled-components/macro'
import { transparentize } from 'polished'
import media from 'styles/mixins/media'
import { StaticLoader } from 'components/misc/Loader'
import Thin from 'components/layout/Thin'
import TrackForm from 'components/Form/Track'
import TrackingMap from 'components/Map/Tracking'
import FaqBase from 'components/layout/Faq'
import ChevronDivider from 'components/misc/ChevronDivider'
import { subtitle2, overline } from 'styles/mixins/textstyles'
import Spacer from 'components/misc/Spacer'
import { Caption, H6, H5, Overline, Body1, Subtitle2 } from 'components/layout/text'
import { getVehicleLabel } from 'components/Form/VehicleSelect'
import Icon from 'components/misc/Icon'
import uniqueId from 'lodash/uniqueId'
import PoweredByRoadie from 'components/misc/PoweredByRoadieBadge'
import { buttonComponent } from 'components/layout/buttons'

function capitalize(str) {
  return str[0].toUpperCase() + str.slice(1)
}

function eventLabel(name) {
  return name.split('_').map(capitalize).join(' ')
}

function formatPhone(value) {
  const str = String(value)
  const digits = str.replace(/^1|\D/g, '').substr(0, 10)
  const [, area, prefix, last] = digits.match(/(\d{3})(\d{3})(.*)/)
  return `${area}-${prefix}-${last}`
}

const FixedWidth = styled.div`
  margin: auto;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 97.7rem;

  ${media.tablet`
    padding: 0 4rem;
  `}
`

const ProgressArea = styled.div`
  padding: 4rem 0;
`

const ProgressBar = styled.div`
  height: 2.4rem;
  width: 100%;
  background: ${props => props.theme.colors.greyLight};
  border: 2px solid white;
  border-radius: 3rem;
  display: flex;
  overflow: hidden;
`

const ProgressItem = styled.div`
  margin: 0 -1px;
  flex: 1 1 100%;
  position: relative;

  ${props => !props.dead && props.complete && css`
    background: ${props => props.theme.colors.primaryBase};
  `}

  ${props => props.dead && css`
    background: ${props => props.theme.colors.errorBase};
  `}
`

const Divider = styled(ChevronDivider)`
  height: 100%;
  width: 2.6rem;
  z-index: 1;
`

const ProgressLabels = styled.div`
  width: calc(100% - 4.8rem);
  display: flex;
  justify-content: space-around;
`

const ProgressLabel = styled.div`
  ${props => subtitle2('black', props.current ? 'normal' : 'medium')}
  flex: 1 1 100%;
  text-align: center;
  border-radius: 100vh;
  color: ${props => props.current ? props.theme.colors.greyDarker : props.theme.colors.greyBase};

  ${media('mobile', 'tablet')`
    display: ${props => (props.current || props.alwaysShow) ? 'block' : 'none'};
  `}
`

const TrackingDetails = styled.div`
  display: flex;
  width: 100%;
  padding-top: 2rem;
  padding-bottom: 4rem;

  & > :first-child {
    margin-right: 5.2rem;
    ${media.desktop`
      width: 21.9rem;
    `}
    ${media('mobile', 'tablet')`
      width: 100%;
    `}
  }

  & > :last-child {
    flex-grow: 1;
    ${media('mobile', 'tablet')`
      margin-top: 4rem;
    `}
  }

  ${media('mobile', 'tablet')`
    flex-direction: column;
  `}
`

const TackingInfoItems = styled.div`
  ${media.tablet`
    display: flex;
    flex-wrap: wrap;
  `}
`

const TrackingInfoItemWrapper = styled.div`
  ${media.tablet`
    display: inline-block;
    width: 50%;
  `}
`

const TrackingInfoItem = ({ label, value }) => (
  <TrackingInfoItemWrapper>
    <Overline emphasis="medium">{label}</Overline>
    <Body1>{value}</Body1>
    <Spacer height="1.6rem" />
  </TrackingInfoItemWrapper>
)

const TrackingUpdatesItem = styled.div`
  display: flex;
  padding: ${props => props.tight ? '1.6rem 3.2rem .8rem' : '2.4rem 3.2rem'};

  &:nth-child(even) {
    background: ${props => props.theme.colors.greyLighter};
  }

  &:nth-child(odd) {
    background: white;
  }

  & > * {
    flex: 1 1 100%;
  }

  ${media.mobile`
    flex-direction: column;

    & > :last-child {
      margin-top: .8rem;
      ${props => props.tight && css`
        display: none;
      `}
    }
  `}
`

const TrackingUpdatesTable = styled.div`
  border: 1px solid ${props => props.theme.colors.greyLight};
  border-radius: 8px;
  overflow: hidden;
`

const Footer = styled.div`
  color: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || props.theme.colors.primaryDarker};
  background: ${props => props.theme.colors[props.theme.footerbg] || props.theme.footerbg || props.theme.colors.primaryDarker};
  border-top: 1px solid ${props => props.theme.colors.primaryDarker};
  padding: 3.2rem 0 4rem;

  
  a {
    color: ${props => props.theme.colors.primaryLight};
  }
  a:hover {
    color: ${props => props.theme.colors.primaryLighter};
  }
`

const FooterHeading = styled(H5)`
  color: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
  width: 100%;
  flex-grow: 1;
  border-bottom: 2px solid ${props => transparentize(0.5, props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white')};
  padding-bottom: .8rem;
  margin-bottom: .8rem;
`

const FooterPhone = styled(buttonComponent('a', 'tertiary'))`
  align-self: flex-start;
  color: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
  display: flex;
  align-items: center;
  margin-top: .8rem;

  svg {
    margin-right: .8rem;
  }

  svg * {
    stroke: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
  }

  &:hover {
    display: flex;
  }
`

const TrackingFaqs = styled.div`
  display: flex;
  width: 100%;
  text-align: center;

  & > * {
    flex: 1 1 50%;

    &:first-child {
      margin-right: 1.6rem;
    }
  }

  ${media('mobile', 'tablet')`
    flex-direction: column;
  `}
`

const Faq = styled(FaqBase)`
  > :first-child {
    color: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
    &::before {
      border-color: transparent transparent transparent ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
    }
  }

  > :last-child {
    color: ${props => props.theme.colors[props.theme.footerfg] || props.theme.footerfg || 'white'};
  }
`

const LiveStatus = styled.div`
  ${overline('black', 'medium')}

  text-align: right;
  width: 100%;
  margin-top: .8rem;
`

class TrackMap extends Component {

  state = {
    tracking: null,
    trackingNowFound: false,
    updating: true,
    mapKey: uniqueId(),
  }

  componentDidMount() {
    const { tracking } = this.props.match.params
    
    this.track(tracking)
  }

  componentWillReceiveProps(nextProps) {
    const { tracking } = this.props.match.params
    const { tracking:nextTracking } = nextProps.match.params

    if (nextTracking !== tracking) {
      this.setState({
        tracking: null,
        trackingNowFound: false,
      }, () => {
        this.track(nextTracking)
      })
    }
  }

  hasMapChanged(a, b) {
    if (!!a !== !!b) {
      // went from no tracking to having tracking
      return true
    }
    if (a.route !== b.route) {
      // the route had changed
      return true
    }
    if (a.pickup_location.latitude !== b.pickup_location.latitude || 
        a.pickup_location.longitude !== b.pickup_location.longitude) {
      // the pickup location has changed
      return true
    }
    if (a.delivery_location.latitude !== b.delivery_location.latitude || 
        a.delivery_location.longitude !== b.delivery_location.longitude) {
      // the delivery location has changed
      return true
    }
    const aHasRoadie = !!a.roadie && !!a.roadie.location
    const bHasRoadie = !!b.roadie && !!b.roadie.location
    if (aHasRoadie !== bHasRoadie) {
      // went from no roadie info to having roadie info (or vice versa)
      return true
    }
    if (aHasRoadie) {
      if(a.roadie.location.latitude !== b.roadie.location.latitude || 
         a.roadie.location.longitude !== b.roadie.location.longitude) {
        // the roadie location has changed
        return true
      }
    }

    // nothing changed
    return false
  }

  track(code) {
    this.setState({ updating: true }, () => {
      fetchGigTracking(code).then(tracking => {
        const delivered = !!tracking.events.find(e => e.event_type === 'delivery_confirmed')
        const cancelled = !!tracking.events.find(e => e.event_type === 'gig_cancelled')
        const expired = tracking.state === 'expired'
        const attempted = tracking.state === 'delivery_attempted'
        const live = !delivered && !cancelled && !expired && !attempted
        const nextState = { tracking, live }
        if (this.hasMapChanged(this.state.tracking, tracking)) {
          nextState.mapKey = uniqueId()
        }
        this.setState(nextState)
        setTimeout(() => this.setState({ updating:false }), 1000 * 3)
        if (live) {
          setTimeout(() => this.track(code), 1000 * 15)
        }
        this.props.dispatch({ type: 'CLEAR_FLASH', id: 'tracking' })
      }).catch(() => {
        this.props.dispatch({ type: 'SET_FLASH', flash: {
          id: 'tracking',
          type: 'error',
          message: this.state.tracking ? 'Network Error. Could not load updates.' : 'Delivery not found. Please confirm that your tracking number is correct.'
        }})
        this.setState({ trackingNowFound: true })
        if (this.state.tracking) {
          setTimeout(() => this.track(code), 1000 * 15)
        }
      })
    })
  }

  getLiveMessage() {
    const { live, updating } = this.state

    if (updating) {
      return 'Updating Now...'
    }

    return `Live Updates: ${live ? 'ON' : 'OFF'}`
  }

  renderTrackingInfo(deliveredDate) {
    const { tracking } = this.state
    const { tracking: trackingNumber } = this.props.match.params
    console.log('tracking', tracking)

    const info = []

    if (tracking.pickup_location) {
      info.push(
        <TrackingInfoItem
          key="pickup_location"
          label="Pickup"
          value={`${tracking.pickup_location.city}, ${tracking.pickup_location.state}`}
        />
      )
    }

    if (tracking.delivery_location) {
      info.push(
        <TrackingInfoItem
          key="delivery_location"
          label="Delivery"
          value={`${tracking.delivery_location.city}, ${tracking.delivery_location.state}`}
        />
      )
    }

    if (!deliveredDate && tracking.delivery_deadline) {
      info.push(
        <TrackingInfoItem
          key="delivery_deadline"
          label="Delivery Before"
          value={new Date(tracking.delivery_deadline).toLocaleString('en', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric', timeZoneName: 'short' })}
        />
      )
    }

    if (deliveredDate) {
      info.push(
        <TrackingInfoItem
          key="delivered"
          label="Delivered"
          value={new Date(deliveredDate).toLocaleString('en', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric', timeZoneName: 'short' })}
        />
      )
    }

    info.push(
      <TrackingInfoItem
        key="signature"
        label="Signature Required"
        value={tracking.delivery_confirmation_required ? 'Yes' : 'No'}
      />
    )

    info.push(
      <TrackingInfoItem
        key="tracking_number"
        label="Tracking Number"
        value={trackingNumber.toUpperCase().match(/.{4}/g).join(" - ")}
      />
    )

    if (tracking.roadie) {

      let vehicle = ''

      if (tracking.roadie.vehicle && tracking.roadie.vehicle.color && tracking.roadie.vehicle.category) {
        vehicle = ` (${capitalize(tracking.roadie.vehicle.color)} ${getVehicleLabel(tracking.roadie.vehicle.category).replace(/ \(.*\)/, '')})`
      }

      info.push(
        <TrackingInfoItem
          key="driver"
          label="Driver"
          value={`${tracking.roadie.name}${vehicle}`}
        />
      )
    }

    return (
      <div>
        <H6>Information</H6>
        <Spacer height="2.4rem" />
        <TackingInfoItems>
          {info}
        </TackingInfoItems>
      </div>
    )
  }

  renderTrackingUpdates() {
    const { tracking } = this.state

    const events = tracking.events.reduce((events, event) => {
      if (['pickup_confirmed', 'delivery_details_added', 'delivery_confirmed', 'gig_cancelled'].includes(event.event_type)) {
        events.push(event)
      }
      return events
    }, [])

    if (tracking.created_at) {
      events.push({
        event_type: 'gig_created',
        created_at: tracking.created_at,
      })
    }

    if (tracking.state === 'delivery_attempted') {
      events.unshift({
        event_type: 'delivery_attempted',
        created_at: tracking.updated_at,
      })
    }

    const items = events.map(event => (
      <TrackingUpdatesItem key={event.event_type}>
        <Subtitle2>{eventLabel(event.event_type)}</Subtitle2>
        <Subtitle2 emphasis="low">{new Date(event.created_at).toLocaleString('en', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</Subtitle2>
      </TrackingUpdatesItem>
    ))

    return (
      <div>
        <H6>Tracking Updates</H6>
        <Spacer height="2.4rem" />
        <TrackingUpdatesTable>
          <TrackingUpdatesItem tight>
            <Overline emphasis="medium">Status</Overline>
            <Overline emphasis="medium">Time</Overline>
          </TrackingUpdatesItem>
          {items}
        </TrackingUpdatesTable>
        {this.props.themeName && this.props.themeName !== 'roadie' && (
          <>
            <Spacer height="1.6rem" />
            <PoweredByRoadie />
          </>
        )}
      </div>
    )
  }

  renderFooter() {
    const { theme } = this.props
    const { tracking } = this.state

    const half = Math.ceil(theme.faq.length/2)

    const leftFaq = theme.faq.slice(0, half)
    const rightFaq = theme.faq.slice(half)

    const phone = tracking.sender && tracking.sender.phone_number

    return (
      <Footer>
        <FixedWidth>
          <FooterHeading>Common Questions</FooterHeading>
          <TrackingFaqs>
            <div>
              {leftFaq.map(({ q, a }) => (
                <Faq faintNotch key={`faq-${q}`} title={q}><span dangerouslySetInnerHTML={{__html: a}} /></Faq>
              ))}
            </div>
            <div>
              {rightFaq.map(({ q, a }) => (
                <Faq faintNotch key={`faq-${q}`} title={q}><span dangerouslySetInnerHTML={{__html: a}} /></Faq>
              ))}
            </div>
          </TrackingFaqs>
          {phone && (
            <>
              <Spacer height="3.2rem" />
              <FooterHeading>Still Need Help?</FooterHeading>
              <FooterPhone href={`tel:${phone}`}><Icon symbol="phone" />{formatPhone(phone)}</FooterPhone>
            </>
          )}
        </FixedWidth>
      </Footer>
    )
  }

  renderResults() {
    const { tracking, trackingNowFound, mapKey } = this.state
    
    
    if (tracking) {

      const hasEvents = tracking.events.map(e  => e.event_type)
      
      const deliveryInProgress = hasEvents.includes('pickup_confirmed')
      const delivered = hasEvents.includes('delivery_confirmed')

      const deliveredDate = delivered && new Date(tracking.events.find(e => e.event_type === 'delivery_confirmed').created_at)

      const cancelled = hasEvents.includes('gig_cancelled')
      const expired = tracking.state === 'expired'
      const attempted = tracking.state === 'delivery_attempted'
      const deadGig = cancelled || expired || attempted
      const live = !delivered && !cancelled && !expired && !attempted
      
      return (
        <>
          <ProgressArea>
            <FixedWidth>
              <ProgressBar>
                <ProgressItem complete dead={deadGig} />
                <Divider
                  left={deadGig ? 'errorBase' : 'primaryBase'}
                  right={deadGig ? 'errorBase' : deliveryInProgress ? 'primaryBase' : 'greyLight'}
                  divider={deadGig ? 'errorBase' : 'white'}
                />
                <ProgressItem complete={deliveryInProgress} dead={deadGig} />
                <Divider
                  left={deadGig ? 'errorBase' : deliveryInProgress ? 'primaryBase' : 'greyLight'}
                  right={deadGig ? 'errorBase' : delivered ? 'primaryBase' : 'greyLight'}
                  divider={deadGig ? 'errorBase' : 'white'}
                />
                <ProgressItem complete={delivered} dead={deadGig} />
              </ProgressBar>
              <Spacer height=".8rem" />
              {deadGig ? (
                <>
                <ProgressLabels>
                  <ProgressLabel current>{cancelled ? 'Canceled' : expired ? 'Expired' : attempted ? 'Delivery Attempted' : 'Error'}</ProgressLabel>
                </ProgressLabels>
                <ProgressLabels>
                  <ProgressLabel alwaysShow><Caption emphasis="medium">For more information, contact the sender.</Caption></ProgressLabel>
                </ProgressLabels>
                </>
              ) : (
                <>
                <ProgressLabels>
                  <ProgressLabel current={!deliveryInProgress}>Pickup in Progress</ProgressLabel>
                  <ProgressLabel current={deliveryInProgress && !delivered}>Delivery in Progress</ProgressLabel>
                  <ProgressLabel current={delivered}>Delivered</ProgressLabel>
                </ProgressLabels>
                <ProgressLabels>
                  <ProgressLabel />
                  <ProgressLabel />
                  <ProgressLabel alwaysShow><Caption emphasis="medium">{delivered ? deliveredDate.toLocaleString('en', { hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric', timeZoneName: 'short' }) : ''}</Caption></ProgressLabel>
                </ProgressLabels>
                </>
              )}
            </FixedWidth>
          </ProgressArea>
          <TrackingMap key={mapKey} tracking={tracking} live={live} themeName={this.props.themeName} />
          <FixedWidth>
            <LiveStatus>{this.getLiveMessage()}</LiveStatus>
            <TrackingDetails>
              {this.renderTrackingInfo(delivered ? deliveredDate : null)}
              {this.renderTrackingUpdates()}
            </TrackingDetails>
          </FixedWidth>
          {this.renderFooter()}
        </>
      )
    } else if (trackingNowFound) {
      return <><Spacer height="6.4rem" /><Thin><TrackForm themeName={this.props.themeName} tracking={this.props.match.params.tracking} /></Thin></>
    } else {
      return <StaticLoader />
    }
  }

  render() {
    return (
      <>        
        { this.renderResults() }
      </>
    )
  }
}

export default connect()(withTheme(TrackMap))